import { sanitizeHtml } from "./sanitizeHtml";

const QUOTED_HISTORY_SELECTORS = [
  "#appendonsend",
  "#divRplyFwdMsg",
  "#mail-editor-reference-message-container",
  ".gmail_quote",
  ".yahoo_quoted",
  ".moz-cite-prefix",
  'blockquote[type="cite"]',
];

const findQuotedHistoryStart = (container) => {
  const candidates = QUOTED_HISTORY_SELECTORS.map((selector) =>
    container.querySelector(selector),
  ).filter(Boolean);

  if (!candidates.length) return null;

  const firstCandidate = candidates.reduce((earliest, candidate) =>
    earliest.compareDocumentPosition(candidate) &
    Node.DOCUMENT_POSITION_PRECEDING
      ? candidate
      : earliest,
  );

  const previousElement = firstCandidate.previousElementSibling;
  return previousElement?.tagName === "HR" ? previousElement : firstCandidate;
};

const openLinksInNewTab = (container) => {
  container.querySelectorAll("a[href]").forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });
};

export const EMAIL_ICON_CLASS = "email-inline-icon";
export const EMAIL_BLOCK_IMAGE_CLASS = "email-block-image";
export const EMAIL_IMAGE_MAX_HEIGHT = 260;
const EMAIL_ICON_MAX_SIDE = 48;

const PIXEL_DIMENSION_PATTERN = /^\s*(\d+)(?:px)?\s*$/i;

const parsePixelDimension = (rawDimension) => {
  const match = PIXEL_DIMENSION_PATTERN.exec(rawDimension || "");
  const dimension = match ? Number(match[1]) : null;
  return dimension > 0 ? dimension : null;
};

const readDeclaredDimension = (image, attributeName) => {
  const rawDimension = image.getAttribute(attributeName);
  return rawDimension === null || rawDimension.trim() === "" ? null : rawDimension;
};

const fitImageHeight = (image, width, height, maxImageHeight) => {
  if (!width || !height || height <= maxImageHeight) return;

  const scale = maxImageHeight / height;
  image.setAttribute("width", String(Math.max(1, Math.round(width * scale))));
  image.setAttribute("height", String(maxImageHeight));
};

const prepareImages = (container, maxImageHeight) => {
  container.querySelectorAll("img").forEach((image) => {
    image.setAttribute("loading", "lazy");

    const declaredDimensions = [
      readDeclaredDimension(image, "width"),
      readDeclaredDimension(image, "height"),
    ].filter((rawDimension) => rawDimension !== null);
    const width = parsePixelDimension(image.getAttribute("width"));
    const height = parsePixelDimension(image.getAttribute("height"));
    const isIcon =
      declaredDimensions.length > 0 &&
      declaredDimensions.every((rawDimension) => {
        const pixels = parsePixelDimension(rawDimension);
        return pixels !== null && pixels <= EMAIL_ICON_MAX_SIDE;
      });

    if (isIcon) {
      image.classList.add(EMAIL_ICON_CLASS);
      return;
    }

    image.classList.add(EMAIL_BLOCK_IMAGE_CLASS);
    fitImageHeight(image, width, height, maxImageHeight);
  });
};

const hasVisibleContent = (container) => {
  const probe = container.cloneNode(true);
  probe.querySelectorAll("style").forEach((styleElement) => styleElement.remove());
  return Boolean(probe.textContent.trim()) || Boolean(probe.querySelector("img"));
};

const UNSAFE_CSS_PATTERN = /expression\s*\(|javascript\s*:|vbscript\s*:|behavior\s*:|-moz-binding|@import/i;
const BLOCKED_STYLE_PROPERTIES = [
  "position",
  "z-index",
  "color",
  "background",
  "background-color",
  "background-image",
  "border-color",
  "outline-color",
  "text-decoration-color",
  "column-rule-color",
  "caret-color",
  "fill",
  "stroke",
];
const URL_VALUE_PATTERN = /url\s*\(/i;
const STYLE_RULE_TYPE = 1;
const MEDIA_RULE_TYPE = 4;
export const EMAIL_SCOPE_ATTRIBUTE = "data-email-scope";
let emailScopeSequence = 0;

const removeBlockedProperties = (style) => {
  BLOCKED_STYLE_PROPERTIES.forEach((property) => style.removeProperty(property));
  Array.from(style)
    .filter((property) => URL_VALUE_PATTERN.test(style.getPropertyValue(property)))
    .forEach((property) => style.removeProperty(property));
};

const COLOR_ATTRIBUTES = ["color", "bgcolor", "background", "text", "link", "vlink", "alink"];

const sanitizeInlineStyles = (container) => {
  container.querySelectorAll("[style]").forEach((element) => {
    if (UNSAFE_CSS_PATTERN.test(element.getAttribute("style") || "")) {
      element.removeAttribute("style");
      return;
    }

    removeBlockedProperties(element.style);
    if (!(element.getAttribute("style") || "").trim()) element.removeAttribute("style");
  });

  container
    .querySelectorAll(COLOR_ATTRIBUTES.map((attribute) => `[${attribute}]`).join(", "))
    .forEach((element) => {
      COLOR_ATTRIBUTES.forEach((attribute) => element.removeAttribute(attribute));
    });
};

const MOJIBAKE_PATTERN = /\u00C3[\u0080-\u00BF]|\u00C2[\u00A0-\u00BF]/;
const WIDE_CHARACTER_PATTERN = /[Ā-￿]/;

/**
 * Repara un texto UTF-8 que quedó decodificado como Latin-1 ("miÃ©rcoles").
 *
 * Pasa cuando el correo declaraba un charset de un byte pero traía UTF-8, y
 * el texto se guardó así. Solo actúa si aparecen las secuencias típicas de
 * ese error y todos los caracteres caben en un byte; si la reinterpretación
 * no es UTF-8 válido, devuelve el texto tal cual.
 *
 * @param {string} text Texto o HTML, posiblemente mal decodificado.
 * @returns {string} El texto reparado, o el original si no hacía falta.
 */
export const repairMojibake = (text) => {
  if (!text || !MOJIBAKE_PATTERN.test(text) || WIDE_CHARACTER_PATTERN.test(text)) return text;

  try {
    return decodeURIComponent(escape(text));
  } catch {
    return text;
  }
};

const scopeSelectorList = (selectorText, scopeSelector) =>
  selectorText
    .split(",")
    .map((selector) => selector.trim())
    .filter(Boolean)
    .map((selector) => {
      const descendant = selector.replace(/^(?:html|body)(?![\w-])/i, "").trim();
      return descendant ? `${scopeSelector} ${descendant}` : scopeSelector;
    })
    .join(", ");

const scopeCssRule = (rule, scopeSelector) => {
  if (rule.type === STYLE_RULE_TYPE) {
    removeBlockedProperties(rule.style);
    const declarations = rule.style.cssText.trim();
    return declarations
      ? `${scopeSelectorList(rule.selectorText, scopeSelector)} { ${declarations} }`
      : "";
  }

  if (rule.type === MEDIA_RULE_TYPE) {
    const nestedCss = Array.from(rule.cssRules)
      .map((nestedRule) => scopeCssRule(nestedRule, scopeSelector))
      .filter(Boolean)
      .join("\n");
    return nestedCss ? `@media ${rule.media.mediaText} { ${nestedCss} }` : "";
  }

  return "";
};

const scopeCss = (cssText, scopeSelector) => {
  if (!cssText.trim() || UNSAFE_CSS_PATTERN.test(cssText)) return "";

  const styleElement = document.createElement("style");
  styleElement.setAttribute("media", "not all");
  styleElement.textContent = cssText;

  try {
    document.head.appendChild(styleElement);

    return Array.from(styleElement.sheet?.cssRules || [])
      .map((rule) => scopeCssRule(rule, scopeSelector))
      .filter(Boolean)
      .join("\n");
  } catch {
    return "";
  } finally {
    styleElement.remove();
  }
};

const scopeStyleBlocks = (container, scopeId) => {
  const scopeSelector = `[${EMAIL_SCOPE_ATTRIBUTE}="${scopeId}"]`;

  container.querySelectorAll("style").forEach((styleElement) => {
    const scopedCss = scopeCss(styleElement.textContent || "", scopeSelector);
    if (scopedCss) {
      styleElement.textContent = scopedCss;
    } else {
      styleElement.remove();
    }
  });
};

const EMPTY_EMAIL_HTML = { mainHtml: "", quotedHtml: "", hasVisibleContent: false, scopeId: "" };

/**
 * Sanitiza el HTML de un correo recibido y separa el historial citado.
 *
 * Conserva los estilos de estructura del correo (atributos `style` y
 * bloques `<style>`: fuentes, tamaños, márgenes, alineación, tablas) para que
 * se vea como lo armó quien lo envió. Los bloques se reescriben acotados al
 * contenedor identificado por `scopeId` (atributo `data-email-scope`) para
 * que no se filtren al resto de la pantalla; solo se conservan reglas de
 * estilo y bloques `@media`, no `@font-face` ni `@import`, que cargarían
 * recursos externos elegidos por el correo, y se quita toda declaración con
 * `url()` por el mismo motivo. Se descartan los colores (incluidos los de
 * bordes y subrayados) y fondos, que quedan a cargo del tema de la app (un texto negro sobre fondo
 * blanco del correo sería ilegible en el tema oscuro), incluidos los
 * atributos de color (`<font color>`, `bgcolor`), las propiedades que
 * podrían salirse del mensaje (posición y z-index) y el CSS que podría
 * ejecutar código. Repara el texto que quedó mal decodificado (ver
 * :func:`repairMojibake`). Detecta la cita de mensajes anteriores de
 * Gmail, Outlook, Yahoo, Thunderbird y Apple Mail para poder plegarla, abre
 * los links en otra pestaña y carga las imágenes de forma diferida. Las
 * imágenes chicas (íconos, píxeles) quedan en línea con el texto; el resto
 * pasa a bloque propio con un alto máximo, escalando las dimensiones
 * declaradas para conservar la proporción.
 *
 * @param {string} html HTML del correo con las imágenes inline ya resueltas.
 * @param {{maxImageHeight?: number}} options Alto máximo de las imágenes en
 * bloque, en píxeles.
 * @returns {{mainHtml: string, quotedHtml: string, hasVisibleContent: boolean, scopeId: string}}
 * Cuerpo propio del mensaje, la cita (vacía si no se detectó o si plegarla
 * dejaría el cuerpo vacío), si después de sanitizar quedó algo para mostrar
 * (texto o imágenes) y el identificador al que quedaron acotados los estilos.
 */
export function prepareEmailHtml(html, { maxImageHeight = EMAIL_IMAGE_MAX_HEIGHT } = {}) {
  const sanitizedHtml = sanitizeHtml(repairMojibake(html), { keepStyles: true });
  if (!sanitizedHtml) return EMPTY_EMAIL_HTML;

  emailScopeSequence += 1;
  const scopeId = `email-${emailScopeSequence}`;
  const container = document.createElement("div");
  container.innerHTML = sanitizedHtml;
  openLinksInNewTab(container);
  prepareImages(container, maxImageHeight);
  sanitizeInlineStyles(container);
  scopeStyleBlocks(container, scopeId);

  if (!hasVisibleContent(container)) return EMPTY_EMAIL_HTML;

  const quotedHistoryStart = findQuotedHistoryStart(container);
  if (!quotedHistoryStart) {
    return { mainHtml: container.innerHTML, quotedHtml: "", hasVisibleContent: true, scopeId };
  }

  const quotedRange = document.createRange();
  quotedRange.setStartBefore(quotedHistoryStart);
  quotedRange.setEnd(container, container.childNodes.length);
  const quotedFragment = quotedRange.extractContents();

  if (!hasVisibleContent(container)) {
    container.appendChild(quotedFragment);
    return { mainHtml: container.innerHTML, quotedHtml: "", hasVisibleContent: true, scopeId };
  }

  const quotedContainer = document.createElement("div");
  quotedContainer.appendChild(quotedFragment);
  return {
    mainHtml: container.innerHTML,
    quotedHtml: quotedContainer.innerHTML,
    hasVisibleContent: true,
    scopeId,
  };
}
